// payment-return-tracking.js — conversion tracking on the SERVER-VERIFIED
// Mollie payment return.
//
// WHY HERE: the funnel submit path redirects to Mollie BEFORE any success
// state (Funnel.jsx BookingConfirm — res.checkoutUrl → window.location), so
// the old BookingConfirm-side Ads conversion + GA4 purchase never fired for
// paid bookings, and fired value-0 events for quote requests (customerTotalFor
// returns 0 for indicative prices). Both events now fire exactly once on the
// SPA payment-return landing, only after /api/payments/return verified the
// payment as authorized|paid against the Mollie API.
//
// DATA PATH: return.js appends value/currency/from/to/aircraft to the
// authorized|paid redirect only (returnTrackingQuery). No PII (no email/phone)
// ever goes into the URL — Enhanced Conversions user_data is intentionally
// NOT carried through the return redirect.
//
// VALUE POLICY (deliberate): value is the FULL customer total (totalPrice,
// incl. VAT + extras) even for deposit bookings — we measure booking value,
// not the authorized hold amount.

// Server side (return.js): build the extra query fragment for the
// authorized|paid redirect. Empty string when totalPrice is not a positive
// finite number — old links / fallback redirects then carry no tracking
// params and the client fires nothing (never a value-0 or default-EUR-1
// conversion in Google Ads).
export function returnTrackingQuery(booking) {
  const value = Number(booking?.totalPrice);
  if (!Number.isFinite(value) || value <= 0) return '';
  const parts = ['value=' + encodeURIComponent(value), 'currency=EUR'];
  if (booking?.from) parts.push('from=' + encodeURIComponent(booking.from));
  if (booking?.to) parts.push('to=' + encodeURIComponent(booking.to));
  if (booking?.aircraftName) parts.push('aircraft=' + encodeURIComponent(booking.aircraftName));
  return parts.join('&');
}

// Client side: decide whether the parsed ?payment return state qualifies for
// conversion tracking. Returns null unless status is the server-verified
// "complete", a booking ref is present and value parses to a positive finite
// number. itemName feeds the GA4 purchase items[] entry.
export function parseReturnConversion(pr) {
  if (!pr || pr.status !== 'complete' || !pr.ref) return null;
  const value = Number(pr.value);
  if (!Number.isFinite(value) || value <= 0) return null;
  const itemName = (pr.from && pr.to) ? pr.from + ' - ' + pr.to : (pr.aircraft || 'Charter');
  return { ref: pr.ref, value, itemName };
}

// Client side: fire the Google Ads conversion + GA4 purchase at most once per
// booking ref. Deps are injected (storage/trackConversion/gtag) so the logic
// is testable without a DOM. Returns true when the events were dispatched.
//
// Dedup: BOTH calls carry the SAME ref as transaction_id, and the
// sessionStorage key reuses the exact 'gh_tracked_' + ref schema the old
// BookingConfirm guard used — a booking tracked under either path can never
// double-fire. Storage failures (Safari private mode) degrade to firing
// without dedup rather than never firing.
export function fireReturnConversionOnce(pr, deps = {}) {
  const parsed = parseReturnConversion(pr);
  if (!parsed) return false;
  const key = 'gh_tracked_' + parsed.ref;
  try {
    if (deps.storage) {
      if (deps.storage.getItem(key)) return false;
      deps.storage.setItem(key, '1');
    }
  } catch { /* storage unavailable → fire without dedup */ }
  if (deps.trackConversion) deps.trackConversion(parsed.value, 'EUR', parsed.ref);
  const purchaseParams = {
    transaction_id: parsed.ref,
    value: parsed.value,
    currency: 'EUR',
    items: [{
      item_name: parsed.itemName,
      item_category: 'helicopter-charter',
      quantity: 1,
      price: parsed.value,
    }],
  };
  // ONE entry per run — the gtag() arguments call. It feeds BOTH GA4 (arguments
  // path) AND GTM's gtag interop, which surfaces it as a Custom Event 'purchase'
  // that the 'GH Booking Purchase' tag fires on. DO NOT re-add a second explicit
  // object entry (an {event:'purchase', …} push): GTM would see the same purchase
  // twice and, since that tag counts Count='Every', DOUBLE-COUNT every booking
  // (money/reporting bug — reverted here). The sessionStorage dedup above only
  // stops the FUNCTION re-running; it never stopped the two-entries-per-run.
  if (deps.gtag) deps.gtag('event', 'purchase', purchaseParams);
  return true;
}
